import RecordInsights, { type ReviewFilter } from "./RecordInsights";
import { beijingToday, type ReviewPeriod } from "./useRecordInsights";
import PhaseTwoEditor, {
  phaseTwoKinds,
  recordKindLabels,
  recordSubtypes,
  phaseTwoPayload,
} from "./PhaseTwoEditor";
import { useEffect, useRef, useState } from "react";
import {
  ArrowLeft,
  Search,
  Plus,
  Pencil,
  Trash2,
  X,
  BookOpen,
  Utensils,
  Dumbbell,
  HeartPulse,
  Droplets,
  Target,
} from "lucide-react";
import {
  diariesApi,
  nutritionApi,
  fitnessApi,
  recordsApi,
  resolveApiUrl,
} from "../../api";
import type { AppRoute } from "../../navigation/routes";
import {
  useAppNavigation,
  useAppBackHandler,
} from "../../navigation/AppNavigation";
import { useRecordHistory, type RecordItem } from "./useRecordHistory";
import "./record-center.css";
const noteTypes = [
  ["note", "日常"],
  ["mood", "情绪"],
  ["tech", "技术"],
  ["fitness", "健身笔记"],
  ["gratitude", "记恩"],
  ["grudge", "记仇"],
];
const dimensions = [
  ["body", "身 · 身体"],
  ["heart", "心 · 内心"],
  ["intellect", "智 · 学习"],
  ["craft", "技 · 技能"],
  ["bond", "缘 · 人际"],
  ["purpose", "志 · 方向"],
];
const exercises = [
  ["strength", "力量训练"],
  ["running", "跑步"],
  ["cycling", "骑行"],
  ["swimming", "游泳"],
  ["ball", "球类运动"],
  ["walking", "健走"],
  ["hiit", "HIIT"],
  ["mobility", "拉伸恢复"],
  ["other", "其他运动"],
];
const localDateTime = (value: string) => {
  const d = new Date(value);
  if (!Number.isFinite(d.getTime())) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}.${String(d.getMilliseconds()).padStart(3, "0")}`;
};
interface Props {
  title?: string;
  initialRecordKey?: string;
  initialNoteType?: string;
  onCompose: () => void;
  onChanged?: () => void;
  onPerson?: (id: number) => void;
  onOpenExercise?: () => void;
}
export default function RecordCenter({
  title = "成长记录",
  initialRecordKey,
  initialNoteType = "all",
  onCompose,
  onChanged,
  onPerson,
  onOpenExercise,
}: Props) {
  const navigation = useAppNavigation();
  const [view, setView] = useState<"list" | "summary">("list");
  const [reviewPeriod, setReviewPeriod] = useState<ReviewPeriod>("week");
  const [reviewAnchor, setReviewAnchor] = useState(beijingToday);
  const [type, setType] = useState(initialNoteType === "all" ? "all" : "note"),
    [subtype, setSubtype] = useState(
      initialNoteType === "all" ? "" : initialNoteType,
    );
  const [dimension, setDimension] = useState(""),
    [input, setInput] = useState(""),
    [q, setQ] = useState(""),
    [from, setFrom] = useState(""),
    [to, setTo] = useState("");
  const filters = {
    schema: "2",
    type,
    subtype: ["note", "health", "study"].includes(type) ? subtype : "",
    dimension,
    q,
    from,
    to,
  };
  const filterKey = JSON.stringify(filters);
  const history = useRecordHistory(filters);
  const [selected, setSelected] = useState<RecordItem | null>(null),
    [detailKey, setDetailKey] = useState(""),
    [detailLoading, setDetailLoading] = useState(false),
    [detailError, setDetailError] = useState("");
  const detailSequence = useRef(0);
  const [form, setForm] = useState<Record<string, any> | null>(null),
    [saving, setSaving] = useState(false),
    [deleting, setDeleting] = useState(false),
    [actionError, setActionError] = useState("");
  const editor = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const timer = setTimeout(() => setQ(input.trim()), 300);
    return () => clearTimeout(timer);
  }, [input]);
  useEffect(() => {
    detailSequence.current++;
    setSelected(null);
    setDetailKey("");
    setDetailError("");
    setDetailLoading(false);
  }, [filterKey]);
  const open = async (key: string) => {
    const match =
      /^(note|meal|exercise|health_bp|health_weight|health_sugar|health_hr|health_personal|study_task|study_quiz|study_project|study_exam|review|water):([1-9]\d*)$/.exec(
        key,
      );
    const request = ++detailSequence.current;
    setSelected(null);
    setDetailKey(key);
    setDetailLoading(true);
    setDetailError("");
    setActionError("");
    try {
      if (!match) throw new Error("记录链接无效");
      const response = await recordsApi.get(match[1], Number(match[2]));
      if (request === detailSequence.current) setSelected(response.data);
    } catch (e: any) {
      if (request === detailSequence.current)
        setDetailError(
          e?.response?.data?.detail || e.message || "详情加载失败",
        );
    } finally {
      if (request === detailSequence.current) setDetailLoading(false);
    }
  };
  useEffect(() => {
    const requests = detailSequence;
    if (initialRecordKey) {
      setView("list");
      open(initialRecordKey);
    }
    return () => {
      requests.current++;
    }; /* exact links also work outside the first page */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialRecordKey]);
  const closeEditor = () => {
    if (saving) return;
    if (form && !window.confirm("放弃尚未保存的修改？")) return;
    setForm(null);
    setActionError("");
  };
  const closeDetail = () => {
    detailSequence.current++;
    setSelected(null);
    setDetailKey("");
    setDetailLoading(false);
    setDetailError("");
  };
  useAppBackHandler(() => {
    if (view === "summary") {
      setView("list");
      return true;
    }
    if (form) {
      closeEditor();
      return true;
    }
    if (detailKey) {
      closeDetail();
      return true;
    }
    return false;
  });
  useEffect(() => {
    if (!form) return;
    const previous = document.activeElement as HTMLElement | null;
    editor.current?.focus();
    const keydown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        closeEditor();
      }
      if (e.key === "Tab") {
        const items = editor.current?.querySelectorAll<HTMLElement>(
          "button:not(:disabled),input,select,textarea",
        );
        if (!items?.length) return;
        const first = items[0],
          last = items[items.length - 1];
        if (
          e.shiftKey &&
          (document.activeElement === first ||
            document.activeElement === editor.current)
        ) {
          e.preventDefault();
          last.focus();
        } else if (
          !e.shiftKey &&
          (document.activeElement === last ||
            document.activeElement === editor.current)
        ) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    window.addEventListener("keydown", keydown);
    return () => {
      window.removeEventListener("keydown", keydown);
      previous?.isConnected && previous.focus();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [Boolean(form), saving]);
  const edit = () => {
    if (!selected || selected.can_edit === false) return;
    setActionError("");
    setForm({
      ...selected.data,
      date:
        selected.kind === "note"
          ? localDateTime(selected.data.date)
          : selected.day,
      started_at: localDateTime(selected.data.started_at),
      measured_at: localDateTime(selected.data.measured_at),
      _initialMeasuredAt: localDateTime(selected.data.measured_at),
      _sourceMeasuredAt: selected.data.measured_at,
      sets: (selected.data.sets || []).map((s: any) => ({ ...s })),
    });
  };
  const field = (name: string, value: any) =>
    setForm((previous) => (previous ? { ...previous, [name]: value } : null));
  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selected || !form || saving) return;
    setSaving(true);
    setActionError("");
    try {
      if (phaseTwoKinds.has(selected.kind)) {
        await recordsApi.update(
          selected.kind,
          selected.id,
          phaseTwoPayload(selected.kind, form),
        );
      } else if (selected.kind === "note") {
        if (!form.content.trim()) throw new Error("请输入笔记内容");
        await diariesApi.update(
          selected.id,
          form.content,
          form.date === localDateTime(selected.data.date)
            ? selected.data.date
            : new Date(form.date).toISOString(),
        );
      } else if (selected.kind === "meal")
        await nutritionApi.updateMeal(selected.id, {
          food_name: form.food_name,
          meal_type: form.meal_type,
          amount: Number(form.amount),
          calories: Number(form.calories),
          date: form.date,
          note: form.note,
        });
      else
        await recordsApi.updateExercise(selected.id, {
          title: form.title,
          exercise_type: form.exercise_type,
          started_at:
            form.started_at === localDateTime(selected.data.started_at)
              ? selected.data.started_at
              : new Date(form.started_at).toISOString(),
          duration_minutes: Number(form.duration_minutes),
          calories: Number(form.calories),
          distance_km: form.distance_km === "" ? null : form.distance_km,
          average_heart_rate:
            form.average_heart_rate === "" ? null : form.average_heart_rate,
          intensity: form.intensity,
          feeling: Number(form.feeling),
          note: form.note,
          sets: form.sets,
        });
      setForm(null);
      if (selected.kind === "water" && Number(form.water_cups) === 0)
        closeDetail();
      else await open(selected.key);
      await history.reload();
      onChanged?.();
    } catch (e: any) {
      setActionError(
        e?.response?.data?.detail || e.message || "保存失败，请重试",
      );
    } finally {
      setSaving(false);
    }
  };
  const remove = async () => {
    if (
      !selected ||
      selected.can_delete === false ||
      deleting ||
      !window.confirm(
        selected.kind === "water"
          ? "确认清零当天饮水？餐食数据保留。"
          : `确认删除这条${recordKindLabels[selected.category || selected.kind] || ""}记录？删除后无法恢复。`,
      )
    )
      return;
    setDeleting(true);
    setActionError("");
    try {
      if (phaseTwoKinds.has(selected.kind))
        await recordsApi.remove(selected.kind, selected.id);
      else if (selected.kind === "note") await diariesApi.delete(selected.id);
      else if (selected.kind === "meal")
        await nutritionApi.deleteMeal(selected.id);
      else await fitnessApi.deleteExerciseSession(selected.id);
      closeDetail();
      await history.reload();
      onChanged?.();
    } catch (e: any) {
      setActionError(e?.response?.data?.detail || "删除失败，请重试");
    } finally {
      setDeleting(false);
    }
  };
  const reset = () => {
    setType("all");
    setSubtype("");
    setDimension("");
    setInput("");
    setQ("");
    setFrom("");
    setTo("");
  };
  const groups = history.rows.reduce<Record<string, RecordItem[]>>(
    (result, row) => {
      (result[row.day] ||= []).push(row);
      return result;
    },
    {},
  );
  const viewPeriodRecords = (filters: ReviewFilter) => {
    closeDetail();
    setType(filters.type || "all");
    setSubtype("");
    setDimension(filters.dimension || "");
    setFrom(filters.from);
    setTo(filters.to);
    setInput("");
    setQ("");
    setView("list");
  };
  const numeric = (
    name: string,
    label: string,
    min = 0,
    max = 50000,
    step = "1",
    optional = false,
  ) => (
    <label>
      {label}
      <input
        type="number"
        min={min}
        max={max}
        step={step}
        required={!optional}
        value={form?.[name] ?? ""}
        onChange={(e) => field(name, e.target.value)}
      />
    </label>
  );
  return (
    <section
      className={`record-center ${detailKey ? "rc-selected" : ""} ${view === "summary" ? "rc-summary-open" : ""}`}
      aria-label="统一记录中心"
    >
      <header className="rc-heading">
        <div>
          <h1>{title}</h1>
          <p>
            {view === "summary"
              ? "按周、按月，回顾自己的变化。"
              : "生活里的每一笔，都在这里。"}
          </p>
        </div>
        <button className="rc-primary" onClick={onCompose}>
          <Plus size={18} />
          随手记录
        </button>
      </header>
      <div className="rc-views" aria-label="回顾视图">
        <button
          aria-pressed={view === "list"}
          disabled={Boolean(form) || saving || deleting}
          onClick={() => setView("list")}
        >
          全部记录
        </button>
        <button
          aria-pressed={view === "summary"}
          disabled={Boolean(form) || saving || deleting}
          onClick={() => setView("summary")}
        >
          周月回顾
        </button>
      </div>
      {view === "summary" && (
        <RecordInsights
          period={reviewPeriod}
          anchor={reviewAnchor}
          onPeriod={setReviewPeriod}
          onAnchor={setReviewAnchor}
          onViewRecords={viewPeriodRecords}
          onCompose={onCompose}
        />
      )}
      <div className="rc-filters">
        <label className="rc-search">
          <Search size={18} />
          <input
            aria-label="搜索全部历史记录"
            placeholder="搜索记录内容、指标、学习途径或目标"
            value={input}
            maxLength={100}
            onChange={(e) => setInput(e.target.value)}
          />
        </label>
        <div className="rc-types" aria-label="记录类型">
          {[
            ["all", "全部"],
            ["note", "笔记"],
            ["meal", "饮食"],
            ["exercise", "运动"],
            ["health", "健康"],
            ["study", "学习"],
            ["review", "复盘"],
            ["water", "饮水"],
          ].map(([key, label]) => (
            <button
              key={key}
              aria-pressed={type === key}
              onClick={() => {
                setType(key);
                setSubtype("");
              }}
            >
              {label}
            </button>
          ))}
        </div>
        <div className="rc-filter-fields">
          {["note", "health", "study"].includes(type) && (
            <label>
              {type === "note"
                ? "笔记类型"
                : type === "health"
                  ? "测量类型"
                  : "学习类型"}
              <select
                aria-label={
                  type === "note"
                    ? "笔记类型"
                    : type === "health"
                      ? "测量类型"
                      : "学习类型"
                }
                value={subtype}
                onChange={(e) => setSubtype(e.target.value)}
              >
                <option value="">
                  全部
                  {type === "note"
                    ? "笔记"
                    : type === "health"
                      ? "测量"
                      : "学习"}
                </option>
                {(type === "note" ? noteTypes : recordSubtypes[type] || []).map(
                  ([key, label]) => (
                    <option key={key} value={key}>
                      {label}
                    </option>
                  ),
                )}
              </select>
            </label>
          )}
          <label>
            人生六维
            <select
              aria-label="按六维筛选"
              value={dimension}
              onChange={(e) => setDimension(e.target.value)}
            >
              <option value="">全部维度</option>
              {dimensions.map(([key, label]) => (
                <option key={key} value={key}>
                  {label}
                </option>
              ))}
            </select>
          </label>
          <label>
            开始日期
            <input
              aria-label="开始日期"
              type="date"
              value={from}
              max={to || undefined}
              onChange={(e) => setFrom(e.target.value)}
            />
          </label>
          <label>
            结束日期
            <input
              aria-label="结束日期"
              type="date"
              value={to}
              min={from || undefined}
              onChange={(e) => setTo(e.target.value)}
            />
          </label>
          <button className="rc-text" onClick={reset}>
            清除筛选
          </button>
        </div>
      </div>
      <div className="rc-count" aria-live="polite">
        {history.loading && !history.rows.length
          ? "正在查询历史记录…"
          : `共 ${history.total} 条 · 已加载 ${history.rows.length} 条`}
        <button
          className="rc-text"
          disabled={history.loading}
          onClick={() => {
            history.reload();
            if (detailKey) open(detailKey);
          }}
        >
          刷新
        </button>
      </div>
      {history.error && (
        <div className="rc-error" role="alert">
          {history.error}
          <button
            onClick={() =>
              history.cursor && history.rows.length
                ? history.loadMore()
                : history.reload()
            }
          >
            重试
          </button>
        </div>
      )}
      <div className="rc-columns">
        <div className="rc-list" aria-label="全部记录列表">
          {Object.entries(groups).map(([day, rows]) => (
            <section key={day}>
              <h2>{day}</h2>
              {rows.map((row) => {
                const category = row.category || row.kind;
                const Icon =
                  category === "meal"
                    ? Utensils
                    : category === "exercise"
                      ? Dumbbell
                      : category === "health"
                        ? HeartPulse
                        : category === "water"
                          ? Droplets
                          : category === "review"
                            ? Target
                            : BookOpen;
                return (
                  <button
                    key={row.key}
                    className={`rc-row ${selected?.key === row.key ? "active" : ""}`}
                    aria-current={
                      selected?.key === row.key ? "true" : undefined
                    }
                    disabled={deleting || saving}
                    onClick={() => open(row.key)}
                  >
                    <span className="rc-row-meta">
                      <Icon size={16} />
                      {row.label}
                      {row.time && (
                        <time>
                          {new Date(row.time).toLocaleTimeString("zh-CN", {
                            hour: "2-digit",
                            minute: "2-digit",
                            timeZone: "Asia/Shanghai",
                          })}
                        </time>
                      )}
                    </span>
                    <strong>{row.title}</strong>
                    <span className="rc-excerpt">{row.content}</span>
                  </button>
                );
              })}
            </section>
          ))}
          {!history.loading && !history.error && !history.rows.length && (
            <div className="rc-empty">
              <BookOpen size={30} />
              <p>
                {dimension === "purpose"
                  ? "还没有匹配的目标复盘，可以在「志」留下阶段回顾。"
                  : "没有匹配的记录。可以调整筛选，或留下一笔新的记录。"}
              </p>
              <button className="rc-text" onClick={reset}>
                查看全部历史
              </button>
              <button className="rc-text" onClick={onCompose}>
                随手记录
              </button>
            </div>
          )}
          {history.loading && (
            <p className="rc-empty" role="status">
              正在加载…
            </p>
          )}
          {history.cursor && (
            <button
              className="rc-more"
              disabled={history.loading}
              onClick={() => history.loadMore()}
            >
              {history.loading ? "加载中…" : "加载更多历史记录"}
            </button>
          )}
        </div>
        <article className="rc-reading" aria-label="记录详情">
          {detailKey && (
            <button className="rc-text rc-detail-back" onClick={closeDetail}>
              <ArrowLeft size={16} />
              返回记录列表
            </button>
          )}
          {detailLoading ? (
            <p role="status">正在加载详情…</p>
          ) : detailError ? (
            <div className="rc-error" role="alert">
              {detailError}
              <button onClick={() => open(detailKey)}>重试</button>
            </div>
          ) : selected ? (
            <>
              <div className="rc-detail-meta">
                {selected.day} · {selected.label}
                {selected.time &&
                  ` · ${new Date(selected.time).toLocaleTimeString("zh-CN", { hour: "2-digit", minute: "2-digit", timeZone: "Asia/Shanghai" })}`}
              </div>
              <h2>{selected.title}</h2>
              <div className="rc-detail-actions">
                {selected.can_edit !== false && (
                  <button onClick={edit}>
                    <Pencil size={16} />
                    编辑
                  </button>
                )}
                {selected.can_delete !== false && (
                  <button
                    disabled={deleting}
                    className="rc-danger"
                    onClick={remove}
                  >
                    <Trash2 size={16} />
                    {deleting
                      ? "处理中…"
                      : selected.kind === "water"
                        ? "清零饮水"
                        : "删除"}
                  </button>
                )}
                {selected.kind === "exercise" && onOpenExercise && (
                  <button onClick={onOpenExercise}>运动管理</button>
                )}
              </div>
              {selected.data.manage && (
                <button
                  className="rc-text"
                  onClick={() => {
                    const target = selected.data.manage;
                    if (
                      [
                        "health",
                        "pathway",
                        "goals",
                        "nutrition",
                        "exam",
                      ].includes(target.route)
                    ) {
                      navigation.replace("records", {
                        ...navigation.current.params,
                        recordKey: selected.key,
                      });
                      navigation.navigate(
                        target.route as AppRoute,
                        target.params,
                      );
                    }
                  }}
                >
                  查看
                  {selected.category === "study"
                    ? selected.data.manage.route === "exam"
                      ? "知识测验"
                      : "学习途径"
                    : selected.category === "review"
                      ? "关联目标"
                      : selected.category === "water"
                        ? "饮食饮水管理"
                        : "健康管理"}
                </button>
              )}
              {selected.hint && <p className="rc-hint">{selected.hint}</p>}
              {!!selected.data.facts?.length && (
                <div className="rc-facts">
                  {selected.data.facts.map(
                    (fact: { label: string; value: string }) => (
                      <span key={fact.label}>
                        {fact.label} · {fact.value}
                      </span>
                    ),
                  )}
                </div>
              )}
              {actionError && !form && (
                <p className="rc-error" role="alert">
                  {actionError}
                </p>
              )}
              <p className="rc-content">
                {selected.kind === "note"
                  ? selected.data.content
                  : selected.content}
              </p>
              {selected.kind === "meal" && (
                <div className="rc-facts">
                  <span>蛋白质 {selected.data.protein ?? 0} g</span>
                  <span>脂肪 {selected.data.fat ?? 0} g</span>
                  <span>碳水 {selected.data.carbs ?? 0} g</span>
                </div>
              )}
              {selected.kind === "exercise" && (
                <>
                  <div className="rc-facts">
                    <span>{selected.data.intensity_label}</span>
                    {selected.data.distance_km != null && (
                      <span>{selected.data.distance_km} 公里</span>
                    )}
                    {selected.data.average_heart_rate && (
                      <span>平均心率 {selected.data.average_heart_rate}</span>
                    )}
                  </div>
                  {selected.data.sets?.map((s: any, i: number) => (
                    <p key={s.id || i} className="rc-set-detail">
                      {s.exercise_name} · 第 {s.set_number} 组
                      {s.weight_kg != null ? ` · ${s.weight_kg} kg` : ""}
                      {s.reps != null ? ` · ${s.reps} 次` : ""}
                      {s.duration_seconds != null
                        ? ` · ${s.duration_seconds} 秒`
                        : ""}
                    </p>
                  ))}
                </>
              )}
              {selected.data.person_id && onPerson && (
                <button
                  className="rc-text"
                  onClick={() => onPerson(selected.data.person_id)}
                >
                  关联人物 · {selected.data.person_name || "查看人物"}
                </button>
              )}
              {!!selected.data.images?.length && (
                <div className="rc-images">
                  {selected.data.images.map((image: any) => (
                    <a
                      key={image.id}
                      href={resolveApiUrl(image.url)}
                      target="_blank"
                      rel="noreferrer"
                    >
                      <img
                        src={resolveApiUrl(image.url)}
                        alt="记录附件"
                        loading="lazy"
                      />
                    </a>
                  ))}
                </div>
              )}
            </>
          ) : (
            <div className="rc-empty">
              <BookOpen size={32} />
              <p>选择一条记录，回看当时的生活。</p>
            </div>
          )}
        </article>
      </div>
      {form && selected && (
        <div
          className="rc-overlay"
          onClick={(e) => e.currentTarget === e.target && closeEditor()}
        >
          <div
            className="rc-edit-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="rc-editor-title"
            ref={editor}
            tabIndex={-1}
          >
            <header>
              <h2 id="rc-editor-title">
                编辑
                {recordKindLabels[selected.category || selected.kind] || ""}
                记录
              </h2>
              <button
                aria-label="关闭编辑"
                disabled={saving}
                onClick={closeEditor}
              >
                <X size={22} />
              </button>
            </header>
            <form onSubmit={save}>
              {phaseTwoKinds.has(selected.kind) ? (
                <PhaseTwoEditor
                  kind={selected.kind}
                  form={form}
                  field={field}
                />
              ) : selected.kind === "note" ? (
                <>
                  <label>
                    记录时间
                    <input
                      required
                      type="datetime-local"
                      step="any"
                      value={form.date}
                      onChange={(e) => field("date", e.target.value)}
                    />
                  </label>
                  <label>
                    笔记内容
                    <textarea
                      required
                      rows={10}
                      value={form.content}
                      onChange={(e) => field("content", e.target.value)}
                    />
                  </label>
                  <p className="rc-hint">
                    原有笔记类型、人物关联和图片附件会保留。
                  </p>
                </>
              ) : selected.kind === "meal" ? (
                <>
                  <label>
                    食物名称
                    <input
                      required
                      maxLength={100}
                      value={form.food_name}
                      onChange={(e) => field("food_name", e.target.value)}
                    />
                  </label>
                  <div className="rc-form-grid">
                    <label>
                      记录日期
                      <input
                        required
                        type="date"
                        value={form.date}
                        onChange={(e) => field("date", e.target.value)}
                      />
                    </label>
                    <label>
                      餐次
                      <select
                        value={form.meal_type}
                        onChange={(e) => field("meal_type", e.target.value)}
                      >
                        {[
                          ["breakfast", "早餐"],
                          ["lunch", "午餐"],
                          ["dinner", "晚餐"],
                          ["snack", "加餐"],
                        ].map(([key, label]) => (
                          <option key={key} value={key}>
                            {label}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label>
                      份量（克）
                      <input
                        type="number"
                        required
                        min="0.1"
                        max="10000"
                        step="0.1"
                        value={form.amount}
                        onChange={(e) =>
                          setForm((previous) =>
                            previous
                              ? {
                                  ...previous,
                                  amount: e.target.value,
                                  calories:
                                    previous.amount > 0
                                      ? Math.round(
                                          ((previous.calories *
                                            Number(e.target.value)) /
                                            Number(previous.amount)) *
                                            10,
                                        ) / 10
                                      : previous.calories,
                                }
                              : null,
                          )
                        }
                      />
                    </label>
                    {numeric("calories", "热量（千卡）", 0, 50000, "0.1")}
                  </div>
                  <label>
                    备注
                    <textarea
                      maxLength={200}
                      rows={4}
                      value={form.note || ""}
                      onChange={(e) => field("note", e.target.value)}
                    />
                  </label>
                  <p className="rc-hint">
                    修改份量时按比例调整营养数值；热量也可以手动校正。
                  </p>
                </>
              ) : (
                <>
                  <label>
                    训练名称
                    <input
                      required
                      maxLength={120}
                      value={form.title}
                      onChange={(e) => field("title", e.target.value)}
                    />
                  </label>
                  <div className="rc-form-grid">
                    <label>
                      运动类型
                      <select
                        value={form.exercise_type}
                        onChange={(e) => field("exercise_type", e.target.value)}
                      >
                        {exercises.map(([key, label]) => (
                          <option key={key} value={key}>
                            {label}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label>
                      训练时间
                      <input
                        required
                        type="datetime-local"
                        step="any"
                        value={form.started_at}
                        onChange={(e) => field("started_at", e.target.value)}
                      />
                    </label>
                    {numeric("duration_minutes", "时长（分钟）", 1, 1440)}
                    {numeric("calories", "消耗（千卡）")}
                    {numeric(
                      "distance_km",
                      "距离（公里）",
                      0,
                      2000,
                      "0.01",
                      true,
                    )}
                    {numeric(
                      "average_heart_rate",
                      "平均心率",
                      20,
                      250,
                      "1",
                      true,
                    )}
                    <label>
                      强度
                      <select
                        value={form.intensity}
                        onChange={(e) => field("intensity", e.target.value)}
                      >
                        <option value="low">低强度</option>
                        <option value="moderate">中等强度</option>
                        <option value="high">高强度</option>
                      </select>
                    </label>
                    {numeric("feeling", "训练感受（1–5）", 1, 5)}
                  </div>
                  <label>
                    训练笔记
                    <textarea
                      rows={4}
                      value={form.note || ""}
                      onChange={(e) => field("note", e.target.value)}
                    />
                  </label>
                  <fieldset className="rc-sets">
                    <legend>动作与组次</legend>
                    {form.sets.map((row: any, index: number) => (
                      <div className="rc-set-edit" key={index}>
                        <label>
                          动作
                          <input
                            required
                            aria-label={`第${index + 1}组动作`}
                            maxLength={100}
                            value={row.exercise_name}
                            onChange={(e) =>
                              field(
                                "sets",
                                form.sets.map((s: any, i: number) =>
                                  i === index
                                    ? { ...s, exercise_name: e.target.value }
                                    : s,
                                ),
                              )
                            }
                          />
                        </label>
                        {(
                          ["weight_kg", "reps", "duration_seconds"] as const
                        ).map((key, i) => (
                          <label key={key}>
                            {["重量 kg", "次数", "时长 秒"][i]}
                            <input
                              aria-label={`第${index + 1}组${["重量", "次数", "时长"][i]}`}
                              type="number"
                              step={i === 0 ? "0.1" : "1"}
                              min={i === 0 ? 0 : 1}
                              max={i === 0 ? 2000 : i === 1 ? 10000 : 86400}
                              value={row[key] ?? ""}
                              onChange={(e) =>
                                field(
                                  "sets",
                                  form.sets.map((s: any, n: number) =>
                                    n === index
                                      ? { ...s, [key]: e.target.value }
                                      : s,
                                  ),
                                )
                              }
                            />
                          </label>
                        ))}
                        <button
                          type="button"
                          aria-label={`删除第${index + 1}组`}
                          onClick={() =>
                            field(
                              "sets",
                              form.sets.filter(
                                (_: any, i: number) => i !== index,
                              ),
                            )
                          }
                        >
                          <Trash2 size={17} />
                        </button>
                      </div>
                    ))}
                    <button
                      type="button"
                      className="rc-text"
                      disabled={form.sets.length >= 100}
                      onClick={() =>
                        field("sets", [
                          ...form.sets,
                          {
                            exercise_name: "",
                            weight_kg: null,
                            reps: null,
                            duration_seconds: null,
                          },
                        ])
                      }
                    >
                      <Plus size={16} />
                      添加一组
                    </button>
                  </fieldset>
                </>
              )}
              {actionError && (
                <p className="rc-error" role="alert">
                  {actionError}
                </p>
              )}
              <footer>
                <button type="button" disabled={saving} onClick={closeEditor}>
                  取消
                </button>
                <button className="rc-primary" type="submit" disabled={saving}>
                  {saving ? "正在保存…" : "保存修改"}
                </button>
              </footer>
            </form>
          </div>
        </div>
      )}
    </section>
  );
}
